import { hydrateCache, getCachedData } from '@common/core-layer/utils/cache-hydration.utils'
import type { LocationData } from '@common/location-layer/types/location'

import { PORTAL_SETTINGS_QUERY_KEY } from '../constants/portal-settings-query-key'

import type { QueryClient } from '@tanstack/vue-query'

/** Shared prefix for `console.warn` when portal-settings hydration fails — keep in sync with unit assertions. */
export const PORTAL_SETTINGS_LOCATION_HYDRATION_WARN_PREFIX = 'Failed to hydrate location store from portal settings (TanStack cache):'

export interface LocationStorePortalSettingsHydrationDeps {
  store: { hasLocation: boolean }
  setLocationData: (data: LocationData) => void
  fetchLocation: () => Promise<LocationData>
}

/**
 * When the location Pinia store is empty, fills it from the TanStack portal-settings
 * cache entry or by fetching through the same hydrateCache path as other middleware.
 */
export async function hydrateLocationStoreFromPortalSettingsQuery(
  queryClient: QueryClient | undefined,
  location: LocationStorePortalSettingsHydrationDeps
): Promise<void> {
  if (location.store.hasLocation) {
    return
  }

  try {
    const cachedData = queryClient ? getCachedData(queryClient, PORTAL_SETTINGS_QUERY_KEY) : null

    if (cachedData) {
      location.setLocationData(cachedData as LocationData)
    } else if (queryClient) {
      await hydrateCache({
        queryClient,
        queryKey: PORTAL_SETTINGS_QUERY_KEY,
        fetchFn: () => location.fetchLocation(),
      })
    } else {
      await location.fetchLocation()
    }
  } catch (error) {
    console.warn(PORTAL_SETTINGS_LOCATION_HYDRATION_WARN_PREFIX, error)
  }
}
